import { Injectable, NgZone, OnDestroy, Optional } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class ClientInstanceService implements OnDestroy {
  private static readonly STORAGE_KEY = 'loda.clientId';
  private static readonly CLAIM_STORAGE_PREFIX = 'loda.clientId.claim.';
  private static readonly HEARTBEAT_INTERVAL_MS = 2000;
  private static readonly CLAIM_FRESHNESS_WINDOW_MS = 6000;

  readonly clientId: string;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _beforeUnloadHandler: (() => void) | null = null;
  private _claimOwned = false;

  constructor(@Optional() private readonly _ngZone?: NgZone) {
    this.clientId = this.getOrCreateClientId();
    this.startHeartbeat();
  }

  private getOrCreateClientId(): string {
    try {
      const storedClientId = sessionStorage.getItem(ClientInstanceService.STORAGE_KEY);
      if (storedClientId && !this.hasFreshClaim(storedClientId)) {
        return storedClientId;
      }

      const clientId = uuidv4();
      sessionStorage.setItem(ClientInstanceService.STORAGE_KEY, clientId);
      return clientId;
    } catch {
      return uuidv4();
    }
  }

  ngOnDestroy(): void {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    if (this._beforeUnloadHandler && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    if (this._claimOwned) {
      this.removeClaim();
      this._claimOwned = false;
    }
  }

  private startHeartbeat(): void {
    if (!this._ngZone) {
      return;
    }

    const heartbeat = () => this.publishClaim();
    this._ngZone.runOutsideAngular(heartbeat);
    this._heartbeatTimer = this._ngZone.runOutsideAngular(() =>
      setInterval(heartbeat, ClientInstanceService.HEARTBEAT_INTERVAL_MS)
    );
    if (typeof window !== 'undefined') {
      this._beforeUnloadHandler = () => this.removeClaim();
      window.addEventListener('beforeunload', this._beforeUnloadHandler);
    }
  }

  private claimStorageKey(clientId: string): string {
    return `${ClientInstanceService.CLAIM_STORAGE_PREFIX}${clientId}`;
  }

  private hasFreshClaim(clientId: string): boolean {
    try {
      const rawClaim = localStorage.getItem(this.claimStorageKey(clientId));
      if (!rawClaim) {
        return false;
      }
      const claim = JSON.parse(rawClaim) as { timestamp?: number };
      return typeof claim.timestamp === 'number' &&
        Date.now() - claim.timestamp < ClientInstanceService.CLAIM_FRESHNESS_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private publishClaim(): void {
    try {
      localStorage.setItem(
        this.claimStorageKey(this.clientId),
        JSON.stringify({ timestamp: Date.now() })
      );
      this._claimOwned = true;
    } catch {
      // Storage access is optional; sessionStorage remains the continuity fallback.
    }
  }

  private removeClaim(): void {
    try {
      localStorage.removeItem(this.claimStorageKey(this.clientId));
    } catch {
      // Storage access is optional; sessionStorage remains the continuity fallback.
    }
  }
}
